// MePage.jsx
// The signed-in "my garden" page at /me. Users sign in with the same email +
// password as the Leaflings iOS app (Firebase Auth is shared) and see their
// own window and full collection — including everything a visitor can't see
// when their profile is private.

// Packages, Dependencies, and Shared Libraries
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth } from "../Firebase";
import { isUserPublic, openInNewTab } from "../sharedFunctions";
import {
  getUserFromDatabase,
  getPlantsByOwner,
  getSpeciesFromDatabase,
} from "../DatabaseOperations";

// React Components
import BioPageHeader from "../BioPage/Header/BioPageHeader";
import Loading from "../MiscPages/Loading";
import PlantImage from "../Sprite/PlantImage";
import ShareBar from "../SharedComponents/ShareBar";
import WindowScene, {
  DEFAULT_ROOM,
  computeWindowPlacement,
} from "../WindowPage/WindowScene";

// Constants
import * as constants from "../constants";

// Friendly messages for the Firebase Auth error codes users actually hit
const AUTH_ERRORS = {
  "auth/invalid-email": "That doesn't look like an email address.",
  "auth/user-not-found":
    "No Leaflings account uses that email. Sign up in the iPhone app first!",
  "auth/wrong-password": "That password doesn't match — try again.",
  "auth/invalid-credential": "That email and password don't match — try again.",
  "auth/too-many-requests":
    "Too many tries — wait a moment and try again.",
};

/*******************************************************************************
 * @func SignInCard (React Component)
 * The email + password form shown when nobody is signed in. Uses the same
 * Firebase Auth project as the iOS app, so app credentials just work.
 *
 * @param None
 * @return JSX Element
 *
 * @notes Guest (anonymous) app accounts have no email — they need to add one
 * in the app before they can sign in here
 *
 ******************************************************************************/
const SignInCard = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    signInWithEmailAndPassword(auth, email.trim(), password).then(
      () => setBusy(false), // Auth listener re-renders the page
      (reason) => {
        setBusy(false);
        setError(
          AUTH_ERRORS[reason?.code] ||
            "We couldn't sign you in — please try again."
        );
      }
    );
  };

  return (
    <div className="mx-auto max-w-[420px] px-4 pt-10 pb-16 font-global text-leaf-brown">
      <h1 className="text-3xl font-extrabold text-leaf-green-dark text-center">
        See your garden
      </h1>
      <p className="text-center text-sm mt-2 mb-6 text-leaf-brown-soft">
        Sign in with the same email and password you use in the Leaflings
        app to view your window and plants online.
      </p>
      <form
        onSubmit={handleSubmit}
        className="bg-global-accent rounded-3xl shadow-card border border-leaf-brown/10 p-6 flex flex-col gap-3"
      >
        <label className="text-sm font-bold" htmlFor="me-email">
          Email
        </label>
        <input
          id="me-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-xl border border-leaf-brown/20 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-leaf-green"
        />
        <label className="text-sm font-bold" htmlFor="me-password">
          Password
        </label>
        <input
          id="me-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="rounded-xl border border-leaf-brown/20 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-leaf-green"
        />
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className="mt-2 bg-leaf-green-dark text-white font-bold rounded-full px-6 py-2.5 shadow-card hover:bg-leaf-green transition-colors disabled:opacity-60"
        >
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </form>
      <p className="text-center text-xs text-leaf-brown-soft mt-4">
        New to Leaflings, or using a guest account? Grab the iPhone app and
        create your account there first.
      </p>
      <div className="text-center mt-3">
        <button
          className="text-leaf-green-dark font-bold text-sm underline underline-offset-4 hover:text-leaf-green transition-colors"
          onClick={() => openInNewTab(constants.APP_STORE_URL)}
        >
          Get Leaflings for iPhone
        </button>
      </div>
    </div>
  );
}; // End SignInCard

/*******************************************************************************
 * @func MePage (React Component)
 * The /me route: sign-in gate, then the signed-in user's own window scene,
 * room switcher, full plant collection, and share tools. Private profiles see
 * everything here (it's their own data) plus a nudge explaining that visitors
 * can't until they flip on a public profile in the app.
 *
 * @param None
 * @return JSX Element
 *
 * @notes Anonymous auth sessions (created by the game's guest multiplayer)
 * are treated as signed out
 *
 ******************************************************************************/
const MePage = () => {
  const navigate = useNavigate();

  // Hooks
  const [authUser, setAuthUser] = useState(undefined); // undefined = checking
  const [userDoc, setUserDoc] = useState(null);
  const [plants, setPlants] = useState(null);
  const [speciesMap, setSpeciesMap] = useState(null);
  const [error, setError] = useState("");
  const [roomIndex, setRoomIndex] = useState(0);

  useEffect(() => {
    document.title = "My garden | Leaflings";
    return () => {
      document.title = "Leaflings — Your Plants, Brought to Life";
    };
  }, []);

  // Track the auth session (shared with the game's guest auth, which we
  // ignore — an anonymous user can't have a garden)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user && !user.isAnonymous ? user : null);
    });
    return unsubscribe;
  }, []);

  // Once signed in, load the garden
  useEffect(() => {
    if (!authUser) {
      setUserDoc(null);
      setPlants(null);
      setSpeciesMap(null);
      return;
    }
    setError("");
    Promise.all([
      getUserFromDatabase(authUser.uid),
      getPlantsByOwner(authUser.uid),
    ]).then(
      ([user, plantDocs]) => {
        setUserDoc(user);
        setPlants(plantDocs);
        const uniqueSpecies = [...new Set(plantDocs.map((p) => p.species))];
        Promise.allSettled(
          uniqueSpecies.map((speciesId) =>
            getSpeciesFromDatabase(speciesId).then((data) => [speciesId, data])
          )
        ).then((results) => {
          const map = {};
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              const [speciesId, data] = result.value;
              map[speciesId] = data;
            }
          });
          setSpeciesMap(map);
        });
      },
      () => {
        setError(
          "We couldn't load your garden — your account may not be fully set " +
            "up yet. Open the Leaflings app and try again."
        );
      }
    );
  }, [authUser]);

  // Still figuring out whether anyone is signed in
  if (authUser === undefined) {
    return (
      <React.Fragment>
        <BioPageHeader />
        <Loading />
      </React.Fragment>
    );
  }

  if (!authUser) {
    return (
      <React.Fragment>
        <BioPageHeader />
        <SignInCard />
      </React.Fragment>
    );
  }

  if (error !== "") {
    return (
      <React.Fragment>
        <BioPageHeader />
        <div className="mx-auto max-w-[480px] px-4 pt-10 text-center font-global text-leaf-brown">
          <p>{error}</p>
          <button
            className="mt-4 text-leaf-green-dark font-bold underline underline-offset-4"
            onClick={() => signOut(auth)}
          >
            Sign out
          </button>
        </div>
      </React.Fragment>
    );
  }

  if (!userDoc || !plants || !speciesMap) {
    return (
      <React.Fragment>
        <BioPageHeader />
        <Loading />
      </React.Fragment>
    );
  }

  return (
    <SignedInGarden
      userDoc={userDoc}
      authUser={authUser}
      plants={plants}
      speciesMap={speciesMap}
      roomIndex={roomIndex}
      setRoomIndex={setRoomIndex}
      navigate={navigate}
    />
  );
}; // End MePage

/*******************************************************************************
 * @func SignedInGarden (React Component)
 * The signed-in body of /me: greeting, window scene, share tools, and the
 * whole collection grid.
 *
 * @param userDoc, authUser, plants, speciesMap, roomIndex, setRoomIndex,
 * navigate
 * @return JSX Element
 *
 * @notes Separated so hooks in MePage stay unconditional
 *
 ******************************************************************************/
const SignedInGarden = ({
  userDoc,
  authUser,
  plants,
  speciesMap,
  roomIndex,
  setRoomIndex,
  navigate,
}) => {
  const rooms =
    Array.isArray(userDoc?.rooms) && userDoc.rooms.length > 0
      ? userDoc.rooms
      : [DEFAULT_ROOM];
  const currentRoom = rooms[Math.min(roomIndex, rooms.length - 1)];

  const { placedPlants } = useMemo(
    () => computeWindowPlacement(plants, speciesMap, currentRoom, rooms),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plants, speciesMap, currentRoom]
  );

  const isPublic = isUserPublic(userDoc);
  const gardenerName = userDoc.name || "gardener";
  const windowUrl = `https://reallifeplants.com/window/${authUser.uid}`;

  return (
    <React.Fragment>
      <BioPageHeader />
      <div className="mx-auto max-w-[600px] px-3 pb-16 font-global text-leaf-brown">
        {/* Greeting */}
        <div className="pl-2 pt-3 text-leaf-green-dark text-[21px]">
          <span
            className="font-header -rotate-1 inline-block"
            style={{ textShadow: "0 1px 0 rgba(255,255,255,0.7)" }}
          >
            Welcome back,
          </span>
        </div>
        <div className="flex items-end justify-between px-2 pb-3 flex-wrap gap-2">
          <h1 className="text-3xl font-extrabold text-leaf-green-dark leading-tight">
            {gardenerName}
          </h1>
          <div className="flex gap-2 text-xs font-semibold text-leaf-brown-soft pb-1">
            {Number.isFinite(userDoc.level) && (
              <span className="bg-white rounded-full px-3 py-1 shadow-sm border border-leaf-brown/10">
                Level {userDoc.level}
              </span>
            )}
            <span className="bg-white rounded-full px-3 py-1 shadow-sm border border-leaf-brown/10">
              {plants.length} {plants.length === 1 ? "plant" : "plants"}
            </span>
          </div>
        </div>

        {/* Room switcher */}
        {rooms.length > 1 && (
          <div className="flex items-center gap-1.5 bg-leaf-green-soft rounded-full p-1 mb-3 mx-auto max-w-[360px] text-sm border border-leaf-green/25 shadow-sm">
            {rooms.map((room, index) => (
              <button
                key={room.id || index}
                className={`flex-1 rounded-full px-3 py-1 font-semibold transition-all ${
                  index === roomIndex
                    ? "bg-leaf-green-dark text-white shadow-sm"
                    : "text-leaf-green-deep hover:bg-white/60"
                }`}
                onClick={() => setRoomIndex(index)}
              >
                {room.name || `Room ${index + 1}`}
              </button>
            ))}
          </div>
        )}

        {/* The window scene */}
        <div className="mx-auto max-w-[440px]">
          <WindowScene
            room={currentRoom}
            plants={placedPlants}
            speciesMap={speciesMap}
            interactive={isPublic}
          />
        </div>

        {/* Sharing */}
        {isPublic ? (
          <div className="mt-4 bg-global-accent rounded-3xl shadow-card border border-leaf-brown/10 p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm">
              <div className="font-extrabold text-leaf-green-dark">
                Your window is public 🎉
              </div>
              <div className="text-leaf-brown-soft text-xs mt-0.5">
                Anyone with the link can peek through it.
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <ShareBar
                title={`${gardenerName}'s window on Leaflings`}
                text={`Peek through my window and meet my Leaflings!`}
                url={windowUrl}
                label="Share my window"
              />
              <button
                className="bg-white text-leaf-green-dark font-bold text-sm rounded-full px-5 py-2 shadow-card border border-leaf-green/25 hover:bg-leaf-green-soft transition-colors"
                onClick={() => navigate("/window/" + authUser.uid)}
              >
                View public page →
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-3xl p-4 text-sm">
            <span className="font-extrabold">Your window is private.</span>{" "}
            Only you can see this page. To share your window and plant pages
            with friends, turn on{" "}
            <span className="font-bold">Public profile</span> in the Leaflings
            app's settings.
          </div>
        )}

        {/* Full collection */}
        <div className="mt-8">
          <h2 className="text-lg font-extrabold text-leaf-green-dark px-2 mb-2">
            All your plants
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {plants.map((plant) => {
              const species = speciesMap[plant.species];
              const clickable = isPublic && species;
              return (
                <div
                  key={plant.id}
                  className={`bg-global-accent rounded-2xl shadow-card border border-leaf-brown/10 p-2 pt-3 text-center transition-all ${
                    clickable
                      ? "cursor-pointer hover:-translate-y-0.5 hover:bg-leaf-cream"
                      : ""
                  }`}
                  onClick={
                    clickable
                      ? () => navigate("/plant/" + plant.id)
                      : undefined
                  }
                >
                  {species && plant.sprite ? (
                    <PlantImage
                      plant={plant}
                      species={species}
                      onLoadCallback={() => {}}
                      width="100%"
                    />
                  ) : (
                    <div className="text-4xl py-6">🌱</div>
                  )}
                  <div className="text-xs font-bold mt-1 truncate">
                    {(plant.name && plant.name.trim()) ||
                      species?.commonName ||
                      "Leafling"}
                  </div>
                </div>
              );
            })}
            {plants.length === 0 && (
              <div className="col-span-3 sm:col-span-4 text-center text-sm text-leaf-brown-soft py-8">
                No plants yet — add your first Leafling in the app! 🌱
              </div>
            )}
          </div>
        </div>

        {/* Account row */}
        <div className="mt-10 text-center text-sm text-leaf-brown-soft">
          Signed in as {authUser.email}
          {" · "}
          <button
            className="underline cursor-pointer hover:text-leaf-brown transition-colors"
            onClick={() => signOut(auth)}
          >
            Sign out
          </button>
        </div>
      </div>
    </React.Fragment>
  );
}; // End SignedInGarden

export default MePage;
